import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { messageDuCode, normalizeLang } from "../../../lib/apiErrorMessage";

/** The full server list stays accessible; no local eligibility rules or filtering. */
export function SendBlockers({
  blockers,
  children,
  presentation = "expanded",
}: {
  blockers: readonly { code: string }[];
  children?: ReactNode;
  presentation?: "expanded" | "compact";
}) {
  const { t, i18n } = useTranslation();
  if (!blockers.length) return null;
  const messages = blockers.map(
    ({ code }) =>
      messageDuCode(code, normalizeLang(i18n.language)) ?? t("flow.blocker_unavailable"),
  );
  if (presentation === "compact") {
    return (
      <aside
        aria-label={t("preparation_context.blocked")}
        className="mb-3 border-l-4 border-amber-500 bg-amber-50 px-3 text-amber-900"
      >
        <details>
          <summary
            aria-live="polite"
            className="min-h-11 cursor-pointer py-3 text-sm font-semibold focus-visible:ring-2"
          >
            {t("flow.blocked_count", { count: blockers.length })}
          </summary>
          <ul className="space-y-2 pb-3 text-sm">
            {messages.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </details>
      </aside>
    );
  }
  return (
    <aside
      aria-label={t("preparation_context.blocked")}
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
    >
      <p className="font-semibold" aria-live="polite">
        {t("flow.blocked_count", { count: blockers.length })}
      </p>
      <p className="mt-2 text-sm">{messages[0]}</p>
      {messages.length > 1 && (
        <details className="mt-2 text-sm">
          <summary className="min-h-11 cursor-pointer py-3 font-medium focus-visible:ring-2">
            {t("flow.other_blockers", { count: messages.length - 1 })}
          </summary>
          <ul className="space-y-2">
            {messages.slice(1).map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </details>
      )}
      {children && <div className="mt-4">{children}</div>}
    </aside>
  );
}
